import React from 'react';
import moment from 'moment';
import {
  CAvatar,
  CButton,
  CDropdown,
  CDropdownItem,
  CDropdownMenu,
  CDropdownToggle,
  CFormInput,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
} from '@coreui/react-pro';
import { Search, Send, MoreVertical, Paperclip } from 'lucide-react';

// helper
const { getDateLabel } = require('../../../helpers/format-date');
const { getFileType } = require('../../../helpers/get-file-type')

const AppChatDetails = ({
  activeChatRoom, 
  searchQuery,
  setSearchQuery,
  chatRooms,
  showModal,
  setShowModal,
  newMessage,
  setNewMessage,
  endOfMessageRef,
  handleOpenRoom,
  handleKeyDown,
  handleSendMessage,
  openPreview,
  handleAttachmentClick,
  handleImageSelect,
  handleDocumentSelect,
  imageInputRef,
  documentInputRef,
  handleImageChange,
  handleDocumentChange,
  showAttachmentMenu,
  handleDeleteChat
}) => {
  return (
    <div style={{ height: '100%', display: 'flex', backgroundColor: '#f0f2f5' }}>
      {/* Sidebar - Chat List */}
      <div style={{ width: '400px', backgroundColor: 'white', borderRight: '1px solid #e9ecef', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div style={{ padding: '16px', backgroundColor: '#f0f2f5', borderBottom: '1px solid #e9ecef' }}>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0 fw-bold">Chats</h5>
          </div>
          
          {/* Search */}
          <div className="position-relative">
            <Search size={18} className="position-absolute" style={{ left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#8696a0' }} />
            <CFormInput
              type="text"
              placeholder="Search name or phone number"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ paddingLeft: '40px', backgroundColor: 'white', borderRadius: '8px' }}
            />
          </div>
        </div>

        {/* Chat List */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {chatRooms && chatRooms.length > 0 ? (
            chatRooms.map((room) => {
                // Parse last message type and content
                let lastMessageDisplay = room.last_message || 'No messages yet';
                let messageIcon = null;
                
                // Check if room has last_message_type or parse from payload
                const messageType = room.last_message_type || 'text';
                
                if (messageType === 'image') {
                  // If no caption, show "Photo" text
                  if (!room.last_message || room.last_message === '') {
                    lastMessageDisplay = '📷 Photo';
                  } else {
                     messageIcon = (
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ marginRight: '4px', flexShrink: 0 }}>
                        <path
                          d="M2 2h12v12H2V2zm1 1v8.586l3.293-3.293a1 1 0 011.414 0L11 11.586V3H3zm0 10v-1.586l3.293-3.293 3.293 3.293L11 12.586V13H3zm8-6a1 1 0 110-2 1 1 0 010 2z"
                          fill="#667781"
                        />
                      </svg>
                    );
                  }
                } else if (messageType === 'video') {
                  // If no caption, show "Video" text
                  if (!room.last_message || room.last_message === '') {
                    lastMessageDisplay = '🎥 Video';
                  } else {
                    messageIcon = (
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ marginRight: '4px', flexShrink: 0 }}>
                        <path
                          d="M2 3.5A1.5 1.5 0 013.5 2h6A1.5 1.5 0 0111 3.5v9a1.5 1.5 0 01-1.5 1.5h-6A1.5 1.5 0 012 12.5v-9z"
                          fill="#667781"
                        />
                        <path
                          d="M11.5 5.5l2.5-1.5v8l-2.5-1.5"
                          fill="#667781"
                          opacity="0.7"
                        />
                        <path
                          d="M6 5.5L8.5 7.5 6 9.5V5.5z"
                          fill="white"
                        />
                      </svg>
                    );
                  }
                } else if (messageType === 'document') {
                  // If no caption, show filename or "Document" text
                  if (!room.last_message || room.last_message === '') {
                    lastMessageDisplay = '📄 Document';
                  } else {
                    messageIcon = (
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ marginRight: '4px', flexShrink: 0 }}>
                        <path
                          d="M9.5 2H4C3.45 2 3 2.45 3 3v10c0 .55.45 1 1 1h8c.55 0 1-.45 1-1V5.5L9.5 2z"
                          fill="#667781"
                        />
                        <path d="M9.5 2v3.5H13" fill="#667781" opacity="0.3" />
                      </svg>
                    );
                  }
                }

                return (
                  <div
                    key={room.id}
                    onClick={() => handleOpenRoom(room.id)}
                    style={{
                      padding: '12px 16px',
                      cursor: 'pointer',
                      backgroundColor: activeChatRoom?.id === room.id ? '#f0f2f5' : 'transparent',
                      borderBottom: '1px solid #f0f2f5',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (activeChatRoom?.id !== room.id) {
                        e.currentTarget.style.backgroundColor = '#f5f6f6';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (activeChatRoom?.id !== room.id) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    <div className="d-flex gap-3">
                      <div className="position-relative">
                        <CAvatar
                          size="lg"
                          color="secondary"
                          textColor="white"
                          style={{ width: '50px', height: '50px' }}
                        >
                          {room.avatar || room.profile_name?.charAt(0) || 'U'}
                        </CAvatar>
                      </div>
                      
                      <div className="flex-grow-1" style={{ minWidth: 0 }}>
                        <div className="d-flex justify-content-between align-items-start mb-1">
                          <span className="fw-semibold" style={{ fontSize: '16px' }}>
                            {room.profile_name || 'Unknown'}
                          </span>
                          <span style={{ fontSize: '12px', color: room.unreadCount > 0 ? '#25d366' : '#667781' }}>
                            {room.last_message_at ? moment(room.last_message_at).format('HH:mm') || '' : ''}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between align-items-center">
                          <span
                            style={{
                              fontSize: '14px',
                              color: '#667781',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              flex: 1
                            }}
                          >
                            {messageIcon}
                            {lastMessageDisplay}
                          </span>

                          {room?.unread_count > 0 && (
                            <div
                              style={{
                                backgroundColor: '#25d366',
                                color: 'white',
                                borderRadius: '12px',
                                minWidth: '20px',
                                height: '20px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '12px',
                                fontWeight: '500',
                                padding: '0 6px',
                                marginLeft: '8px',
                                flexShrink: 0
                              }}
                            >
                              {room.unread_count > 99 ? '99+' : room.unread_count}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )
              } 
            )
          ) : (
            <div className="text-center p-4" style={{ color: '#667781' }}>
              No chats available
            </div>
          )}
        </div>
      </div>

      {/* Main Chat Area */}
      {activeChatRoom ? (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Chat Header */}
          <div style={{ padding: '12px 16px', backgroundColor: '#f0f2f5', borderBottom: '1px solid #e9ecef' }}>
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center gap-3">
                <CAvatar
                  size="md"
                  color="secondary"
                  textColor="white"
                  style={{ width: '40px', height: '40px' }}
                >
                  {activeChatRoom.avatar || activeChatRoom.profile_name?.charAt(0) || 'U'}
                </CAvatar>
                <div>
                  <div className="fw-semibold">{activeChatRoom.profile_name || activeChatRoom.name || 'Unknown'}</div>
                </div>
              </div>
              
              <div className="d-flex gap-3">
                <CDropdown>
                  <CDropdownToggle caret={false} className="p-2 border-0 bg-transparent">
                    <MoreVertical size={20} color="#54656f" />
                  </CDropdownToggle>
                  <CDropdownMenu>
                    <CDropdownItem className="text-danger" onClick={() => setShowModal(true)}>
                      Delete Chat
                    </CDropdownItem>
                  </CDropdownMenu>
                </CDropdown>
              </div>
            </div>
          </div>

          {/* Messages Area */}
          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '20px',
              backgroundImage: 'linear-gradient(rgba(0,0,0,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.05) 1px, transparent 1px)',
              backgroundSize: '20px 20px',
              backgroundColor: '#efeae2'
            }}
          >
            {activeChatRoom.messages && activeChatRoom.messages.length > 0 ? (
              activeChatRoom.messages.map((message, index) => {
                  let jsonPayload = JSON.parse(message?.payload || '{}');
                  let mediaUrl = '';
                  let documentName = '';

                  if (message.direction == 'inbound') {
                    mediaUrl = jsonPayload?.media_url || '';
                    if (jsonPayload?.type == 'document') {
                      documentName = jsonPayload?.document?.filename || '';
                    }
                  } else {
                    if (jsonPayload?.type == 'image') {
                      mediaUrl = jsonPayload?.image_url || '';
                    } else if (jsonPayload?.type == 'video') {
                      mediaUrl = jsonPayload?.video_url || '';
                    } else if (jsonPayload?.type == 'document') {
                      mediaUrl = jsonPayload?.document_url || '';
                      documentName = jsonPayload?.file_name || '';
                    }
                  }

                  let documentFileType = getFileType(documentName);
                  
                  // Check if we need to show date divider
                  const currentMessageDate = moment(message.timestamp);
                  const previousMessage = index > 0 ? activeChatRoom.messages[index - 1] : null;
                  const previousMessageDate = previousMessage ? moment(previousMessage.timestamp) : null;
                  
                  // Determine if we should show date divider
                  const showDateDivider = !previousMessageDate || !currentMessageDate.isSame(previousMessageDate, 'day');
                  
                 return (
                    <React.Fragment key={message.id}>
                       {/* Date Divider */}
                        {showDateDivider && (
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              margin: '12px 0',
                              position: 'relative'
                            }}
                          >
                            <div
                              style={{
                                backgroundColor: '#e1f4fb',
                                color: '#54656f',
                                fontSize: '12.5px',
                                fontWeight: '500',
                                padding: '6px 12px',
                                borderRadius: '8px',
                                boxShadow: '0 1px 0.5px rgba(0,0,0,0.13)',
                                textTransform: 'uppercase',
                                letterSpacing: '0.3px'
                              }}
                            >
                              {getDateLabel(currentMessageDate)}
                            </div>
                          </div>
                        )}
                      <div
                        key={message.id}
                        className={`d-flex mb-2 ${message.direction === 'outbound' ? 'justify-content-end' : 'justify-content-start'}`}
                      >
                        <div
                          style={{
                            maxWidth: '65%',
                            padding: '8px 12px',
                            borderRadius: '8px',
                            backgroundColor: message.direction === 'outbound' ? '#d9fdd3' : 'white',
                            boxShadow: '0 1px 0.5px rgba(0,0,0,0.13)',
                            position: 'relative'
                          }}
                        >
                          {message.type === 'image' || message.type === 'video' ? (
                            <div>
                              {
                                message.type === 'video' ? (
                                  <video
                                    src={mediaUrl}
                                    style={{
                                      width: '100%',
                                      maxWidth: '350px',
                                      borderRadius: '6px',
                                      display: 'block',
                                      cursor: 'pointer'
                                    }}
                                    controls
                                  />
                                ) : (
                                  <>
                                    <img
                                      src={mediaUrl}
                                      // alt="Shared image"
                                      style={{
                                        width: '100%',
                                        maxWidth: '350px',
                                        borderRadius: '6px',
                                        display: 'block',
                                        cursor: 'pointer'
                                      }}
                                      onClick={() => openPreview('image', mediaUrl)}
                                    />
                                    {/* Image Overlay Actions */}
                                    {
                                      mediaUrl && (
                                        <div
                                          style={{
                                            position: 'absolute',
                                            top: '12px',
                                            right: '8px',
                                            display: 'flex',
                                            gap: '2px'
                                          }}
                                        >
                                          {/* View Button */}
                                          <button
                                            onClick={() => openPreview('image', mediaUrl)}
                                            style={{
                                              backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                              border: 'none',
                                              borderRadius: '50%',
                                              width: '25px',
                                              height: '25px',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              cursor: 'pointer',
                                              transition: 'background-color 0.2s'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.7)'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.5)'}
                                            title="View image"
                                          >
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                                              <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                                            </svg>
                                          </button>
                                          
                                          {/* Download Button */}
                                          <button
                                            onClick={() => {
                                              const link = document.createElement('a');
                                              link.href = mediaUrl;
                                              link.download = `image_${message.id}`;
                                              document.body.appendChild(link);
                                              link.click();
                                              document.body.removeChild(link);
                                            }}
                                            style={{
                                              backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                              border: 'none',
                                              borderRadius: '50%',
                                              width: '25px',
                                              height: '25px',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              cursor: 'pointer',
                                              transition: 'background-color 0.2s'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.7)'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.5)'}
                                            title="Download image"
                                          >
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                                              <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
                                            </svg>
                                          </button>
                                        </div>
                                      )
                                    }
                                  </>
                                )
                              }
                              {message.text && (
                                <div style={{ fontSize: '14.5px', color: '#111b21', wordBreak: 'break-word', padding: '8px 8px 4px' }}>
                                  {message.text}
                                </div>
                              )}
                              <div
                                className="d-flex align-items-center justify-content-end gap-1"
                                style={{ fontSize: '11px', color: '#667781', padding: '4px 8px 4px' }}
                              >
                                <span>{moment(message.time || message.timestamp).format('HH:mm')}</span>
                              </div>
                            </div>
                          ) : message.type == 'document' ? (
                            <div>
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '12px',
                                  padding: '8px',
                                  backgroundColor: message.direction === 'outbound' ? '#caf4c4' : '#f0f2f5',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  minWidth: '250px'
                                }}
                              >
                                {/* Icon Document */}
                                <div
                                  style={{
                                    width: '48px',
                                    height: '48px',
                                    backgroundColor: message.direction === 'outbound' ? '#d9fdd3' : 'white',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0
                                  }}
                                >
                                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <path
                                      d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z"
                                      fill="#667781"
                                    />
                                    <path d="M14 2V8H20" fill="#667781" opacity="0.3" />
                                  </svg>
                                </div>
                                
                                {/* File Info */}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div
                                    style={{
                                      fontSize: '14px',
                                      color: '#111b21',
                                      fontWeight: '500',
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis'
                                    }}
                                  >
                                    {documentName}
                                  </div>
                                  {/* {fileSize && (
                                    <div style={{ fontSize: '12px', color: '#667781', marginTop: '2px' }}>
                                      {fileSize}
                                    </div>
                                  )} */}
                                </div>

                                {/* Action Buttons */}
                                <div style={{ display: 'flex', flexShrink: 0 }}>
                                  {/* View Button */}
                                  <button
                                    onClick={() => openPreview(documentFileType, mediaUrl, documentName)}
                                    style={{
                                      backgroundColor: 'transparent',
                                      border: 'none',
                                      borderRadius: '50%',
                                      width: '25px',
                                      height: '25px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      cursor: 'pointer',
                                      transition: 'background-color 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)'}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                    title="View document"
                                  >
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="#667781">
                                      <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                                    </svg>
                                  </button>
                                  
                                  {/* Download Button */}
                                  <button
                                    onClick={() => {
                                      const link = document.createElement('a');
                                      link.href = mediaUrl;
                                      link.download = documentName;
                                      document.body.appendChild(link);
                                      link.click();
                                      document.body.removeChild(link);
                                    }}
                                    style={{
                                      backgroundColor: 'transparent',
                                      border: 'none',
                                      borderRadius: '50%',
                                      width: '25px',
                                      height: '25px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      cursor: 'pointer',
                                      transition: 'background-color 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)'}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                    title="Download document"
                                  >
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="#667781">
                                      <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
                                    </svg>
                                  </button>
                                </div>
                              </div>
                              
                              {message.text && (
                                <div style={{ fontSize: '14.5px', color: '#111b21', wordBreak: 'break-word', padding: '8px 8px 4px' }}>
                                  {message.text}
                                </div>
                              )}
                              
                              <div
                                className="d-flex align-items-center justify-content-end gap-1"
                                style={{ fontSize: '11px', color: '#667781', padding: '4px 8px 4px' }}
                              >
                                <span>{moment(message.time || message.timestamp).format('HH:mm')}</span>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div style={{ fontSize: '14.5px', color: '#111b21', wordBreak: 'break-word' }}>
                                {message.text}
                              </div>
                              <div
                                className="d-flex align-items-center justify-content-end gap-1 mt-1"
                                style={{ fontSize: '11px', color: '#667781' }}
                              >
                                <span>{moment(message.time || message.timestamp).format('HH:mm')}</span>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </React.Fragment>
                  )
                }
              )
            ) : (
              <div className="text-center" style={{ color: '#667781', marginTop: '50px' }}>
                No messages yet
              </div>
            )}
            <div ref={endOfMessageRef} />
          </div>

          {/* Hidden file inputs */}
          <input
            ref={imageInputRef}
            type="file"
            accept="image/*, video/*"
            style={{ display: 'none' }}
            onChange={handleImageChange}
          />
          <input
            ref={documentInputRef}
            type="file"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
            style={{ display: 'none' }}
            onChange={handleDocumentChange}
          />
          {/* Message Input */}
          <div style={{ padding: '12px 16px', backgroundColor: '#f0f2f5', position: 'relative' }}>
            {/* Attachment Menu Popup */}
            {showAttachmentMenu && (
              <div
                className="attachment-menu-container"
                style={{
                  position: 'absolute',
                  bottom: '70px',
                  left: '16px',
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                  padding: '8px',
                  zIndex: 1000,
                  minWidth: '200px'
                }}
              >
                {/* Document Option */}
                <div
                  onClick={handleDocumentSelect}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f6f6'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <div
                    style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      backgroundColor: '#5f66cd',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                      <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z"/>
                      <path d="M14 2V8H20" opacity="0.3"/>
                    </svg>
                  </div>
                  <div>
                    <div style={{ fontSize: '15px', fontWeight: '500', color: '#111b21' }}>
                      Document
                    </div>
                    <div style={{ fontSize: '13px', color: '#667781' }}>
                      PDF, DOC, XLS, etc.
                    </div>
                  </div>
                </div>

                {/* Image Option */}
                <div
                  onClick={handleImageSelect}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f6f6'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <div
                    style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      backgroundColor: '#d3396d',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                      <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
                    </svg>
                  </div>
                  <div>
                    <div style={{ fontSize: '15px', fontWeight: '500', color: '#111b21' }}>
                      Photos & Videos
                    </div>
                    <div style={{ fontSize: '13px', color: '#667781' }}>
                      JPG, PNG, MP4, etc.
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="d-flex gap-2 align-items-end">
              <div className="attachment-menu-container">
                <CButton
                  color="transparent"
                  className="p-2 border-0"
                  onClick={handleAttachmentClick}
                >
                  <Paperclip size={24} color="#54656f" />
                </CButton>
              </div>
              <div style={{ flex: 1 }}>
                <CFormInput
                  type="text"
                  placeholder="Type a message"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  style={{
                    borderRadius: '8px',
                    padding: '10px 12px',
                    fontSize: '15px'
                  }}
                />
              </div>
              <CButton
                onClick={handleSendMessage}
                style={{
                  backgroundColor: '#25d366',
                  border: 'none',
                  borderRadius: '50%',
                  width: '48px',
                  height: '48px',
                  padding: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Send size={20} color="white" />
              </CButton>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex-grow-1 d-flex flex-column align-items-center justify-content-center" style={{ backgroundColor: 'white' }}>
          <div className="text-center">
            <div
              style={{
                width: '320px',
                height: '320px',
                margin: '0 auto 32px',
                backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 400 400\'%3E%3Ccircle cx=\'200\' cy=\'200\' r=\'180\' fill=\'%2325d366\' opacity=\'0.1\'/%3E%3C/svg%3E")',
                backgroundSize: 'contain',
                backgroundRepeat: 'no-repeat',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <svg width="200" height="200" viewBox="0 0 24 24" fill="none" stroke="#8696a0" strokeWidth="1">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
            </div>
            <h4 style={{ color: '#41525d', marginBottom: '16px' }}>Cloudxier Chat Web</h4>
            <p style={{ color: '#667781', fontSize: '14px', maxWidth: '450px', margin: '0 auto' }}>
              Send and receive messages to our customers.<br />
            </p>
          </div>
        </div>
      )}

      {/* Delete Chat Modal */}
      <CModal visible={showModal} onClose={() => setShowModal(false)} alignment="center">
        <CModalHeader>
          <CModalTitle>Delete Chat?</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <p>Are you sure you want to delete this chat? This action cannot be undone.</p>
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </CButton>
          <CButton
            style={{ backgroundColor: '#ea0038', border: 'none' }}
            onClick={() => {
              handleDeleteChat(activeChatRoom?.id);
            }}
          >
            Delete
          </CButton>
        </CModalFooter>
      </CModal>
    </div>
  );
};

export default AppChatDetails;